import React, { useId, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconAiDocument } from '../../../../shared/icon/icon'
import { utcText } from '../../../../shared/date/local-time'
import { listSessions } from './ai-tools-api'
import { SOURCE_FILTERS, isUnlinked, personOrgCell, listFilters, marksCell, receivedAgo, receivedWindow, sessionRow } from './ai-tools-words'
import { CLIENT_SPEC, CONSUMER_SPEC, ClientAppFilter, ConsumerStatusFilters, FilterSelect, LOGIN_SPEC, ORGANIZATION_SPEC, OrganizationFilter, PersonChip, RECEIVED_SPEC, ReceivedFilter, STATUS_SPEC, choiceSpec, useClientApps, useListFilters } from './ai-tools-filters'
import { MarkableList, useMarkableList } from './markable-list'
import { useMatchingActions } from './matching-actions'

// The Sessions tab (contract §4.6, §8; R2): every AI tools session, newest
// first, paged by ai's cursor, filtered by organization, source, a
// consumer's marks (by status, and by who processed them) and a person (the
// Person column's filter, a chip in the toolbar), and searched in the grid's
// box. Each row says the person it came from (name over email, their staff
// page) and who it is linked to (or Unlinked), its tools version, how many
// records it has and each consumer's mark; its name opens the session's
// page. Mark…, Delete and select mode are markable-list.js (shared with the
// other tabs and a session's records); this holds what is the sessions':
// filters, columns, links and the grid's own queryKey.
//
// consumers, onMarked, onNeedConsumers: as useMarkableList.

const QUERY_KEY = 'sessions'
// The one sortable column: the server orders by when it received the session.
const ORDER_FIELD = 'receivedText'
const FAILED = "The sessions couldn't be loaded."
const markLabel = row => `session ${row.sessionText}`
const pageOf = row => `/admin/ai-tools/sessions/${row.id}`

const sessionCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      <Link className="fw-semibold text-truncate sw-mono" to={pageOf(row)} onClick={e => e.stopPropagation()}>{row.sessionText}</Link>
      {/* the client app the person used, then the session's records: one
          line, so the list fits a laptop's window (owner 2026-10-29); the
          source, channel and version on hover */}
      <span className="sw-cell-sub text-truncate" title={[row.clientAppText, row.sourceText, row.channelText, row.versionText, row.countsText].filter(Boolean).join(' · ')}>
        {[row.clientAppText, row.recordsText].filter(Boolean).join(' · ')}
      </span>
    </span>
  )
}
// The server cuts an unlinked row's time to the UTC day, by design.
const UNLINKED_RECEIVED = 'The server keeps only the UTC day of an unlinked session, so there is no time of day.'
const receivedCell = cell => {
  const row = cell.data
  if (!row || !row.receivedAt) return ''
  return (
    <span className="ak-name-cell">
      <span title={isUnlinked(row) ? `${utcText(row.receivedAt).slice(0, 10)} UTC. ${UNLINKED_RECEIVED}` : `${row.receivedText} · ${utcText(row.receivedAt)}`}>{receivedAgo(row)}</span>
      {row.runText && <span className="sw-cell-sub" title={row.firstSeenTs ? `${utcText(row.firstSeenTs)} – ${utcText(row.lastSeenTs)}` : undefined}>{row.runText}</span>}
    </span>
  )
}

// The tab's filters in the address; it opens on Needs processing, any time.
const FILTERS = { organization: ORGANIZATION_SPEC, source: choiceSpec(SOURCE_FILTERS), client: CLIENT_SPEC, consumer: CONSUMER_SPEC, status: STATUS_SPEC, login: LOGIN_SPEC, ...RECEIVED_SPEC }

export const SessionsTab = ({ consumers, onMarked, onNeedConsumers }) => {
  const ids = { org: useId(), source: useId(), client: useId() }
  const clientApps = useClientApps()
  const { filters: f, update } = useListFilters(FILTERS)
  const { organization, source, client, consumer, status, login } = f

  // the window is reckoned when the range is picked, not on every render
  const received = useMemo(() => receivedWindow(f), [f])
  const { from, to, problem } = received

  const filters = useMemo(() => ({ ...listFilters({ organization, source, consumer, status, login, client }), from, to }), [organization, source, consumer, status, login, client, from, to])
  const list = useMarkableList({ fetchPage: listSessions, toRow: sessionRow, filters, failedText: FAILED, kind: 'session', labelOf: markLabel, consumers, onMarked, onNeedConsumers, orderField: ORDER_FIELD, blocked: problem, recycleTab: 'sessions' })
  const set = key => list.change(value => update({ [key]: value }))

  const controls = (
    <>
      <OrganizationFilter id={ids.org} value={organization} onChange={set('organization')} />
      <FilterSelect id={ids.source} label="Source" value={source} onChange={set('source')} options={SOURCE_FILTERS} />
      <ClientAppFilter id={ids.client} value={client} onChange={set('client')} apps={clientApps} />
      <ConsumerStatusFilters consumers={consumers} consumer={consumer} status={status} onConsumer={set('consumer')} onStatus={set('status')} />
      <ReceivedFilter value={f} onChange={list.change(update)} />
      <PersonChip login={login} person={list.personOf(login)} onClear={() => set('login')('')} />
    </>
  )
  const actions = useMatchingActions({ list, kind: 'session', filters, fromDay: received.fromDay, toDay: received.toDay })

  return (
    <MarkableList list={list} queryKey={QUERY_KEY} actions={actions} title="Sessions" controls={controls} rowTarget={pageOf}
      emptyState={{ Icon: IconAiDocument, title: 'No sessions', message: 'No AI tools session matches these filters.' }}>
      <ColumnFormat field="sessionText" header="Session" sortable={false} flex={1.4} textMax={220} stacked render={sessionCell} />
      <ColumnFormat field="personText" header="Person" sortable={false} textMax={200} stacked render={personOrgCell} />
      <ColumnFormat field={ORDER_FIELD} header="Received" sortable fit="content" stacked render={receivedCell} />
      <ColumnFormat field="marksText" header="Marks" sortable={false} flex={1.2} render={marksCell} />
    </MarkableList>
  )
}
